// src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { SecurityProvider } from './contexts/SecurityContext';
import './styles/responsive.css'; // إضافة CSS للتجاوب
import './styles/global.css';
// وظيفة الحماية ضد أدوات المطور
function setupSecurityProtection() {
  // تهيئة الأمان في جميع البيئات، مع تطبيق حماية إضافية في الإنتاج
  const isProduction = process.env.NODE_ENV === 'production';
  
  // مراقبة فتح أدوات المطور بالطرق المختلفة
  let devToolsDetected = false;

  // الطريقة 1: كشف فتح أدوات المطور عن طريق حجم النافذة
  const checkDevToolsStatus = () => {
    const widthThreshold = window.outerWidth - window.innerWidth > 160;
    const heightThreshold = window.outerHeight - window.innerHeight > 160;
    
    if (widthThreshold || heightThreshold) {
      if (!devToolsDetected) {
        devToolsDetected = true;
        // إجراءات الحماية هنا
        disableConsoleOutput();
        if (isProduction) {
          // حذف الأشياء الحساسة في وضع الإنتاج
          sanitizeSensitiveData();
        }
      }
    } else {
      devToolsDetected = false;
    }
  };

  // تعطيل الوصول إلى وحدة التحكم
  function disableConsoleOutput() {
    try {
      if (isProduction) {
        // تعطيل كامل في الإنتاج
        console.log = function() {};
        console.info = function() {};
        console.warn = function() {};
        console.debug = function() {};
        console.trace = function() {};
      } else {
        // في وضع التطوير، قم بتصفية البيانات الحساسة فقط
        const originalConsoleLog = console.log;
        console.log = function() {
          for (let i = 0; i < arguments.length; i++) {
            const arg = arguments[i];
            if (typeof arg === 'string' && containsSensitiveInfo(arg)) {
              arguments[i] = '[معلومات محمية]';
            } else if (typeof arg === 'object' && arg !== null) {
              try {
                const stringified = JSON.stringify(arg);
                if (containsSensitiveInfo(stringified)) {
                  arguments[i] = { protected: true };
                }
              } catch (e) {
                // تجاهل أخطاء التحويل
              }
            }
          }
          return originalConsoleLog.apply(console, arguments);
        };
      }
      
      // إعادة تعريف Object.defineProperty لمنع إعادة تعيين وحدة التحكم
      const originalDefineProperty = Object.defineProperty;
      Object.defineProperty = function(obj, prop, descriptor) {
        if (obj === window && (prop === 'console' || prop === '_console')) {
          return obj;
        }
        return originalDefineProperty.call(Object, obj, prop, descriptor);
      };
    } catch (e) {
      // تجاهل الأخطاء
    }
  }
  
  // فحص إذا كانت المعلومات تحتوي على بيانات حساسة
  function containsSensitiveInfo(str) {
    if (!str) return false;
    
    const sensitivePatterns = [
      /api[-_]?key/i,
      /firebase/i,
      /auth[-_]?token/i,
      /key=/i,
      /password/i,
      /secret/i,
      /AIzaSy/i, // جزء من مفتاح Google المعتاد
    ];
    
    return sensitivePatterns.some(pattern => pattern.test(str));
  }
  
  // إزالة البيانات الحساسة من النافذة العالمية
  function sanitizeSensitiveData() {
    try {
      // حذف أي بيانات حساسة مكشوفة في النافذة
      delete window.firebaseConfig;
      delete window.apiKey;
      delete window.authConfig;
      
      // تحديد النافذة وأي عناصر تحتوي على بيانات حساسة في HTML
      if (typeof window.MutationObserver === 'function') {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              // البحث عن عناصر script التي تحتوي على مفاتيح API
              const scripts = document.querySelectorAll('script[src*="key="]');
              scripts.forEach((script) => {
                if (script.src && script.src.includes('key=')) {
                  // بدلاً من محاولة تعديل خاصية src غير القابلة للتهيئة، نقوم بإخفاء المفتاح بطريقة أخرى
                  try {
                    // سنقوم بإنشاء عنصر script جديد مع src معدل وإضافته بدلاً من تعديل الأصلي
                    const secureUrl = script.src.replace(/(key=)([^&]*)/, '$1***PROTECTED***');
                    const newScript = document.createElement('script');
                    
                    // نسخ جميع سمات العنصر الأصلي
                    for (let i = 0; i < script.attributes.length; i++) {
                      const attr = script.attributes[i];
                      if (attr.name !== 'src') {
                        newScript.setAttribute(attr.name, attr.value);
                      }
                    }
                    
                    // تعيين عنوان URL المحمي
                    newScript.src = secureUrl;
                    
                    // إزالة العنصر الأصلي وإضافة العنصر الجديد إذا كان لديه أب
                    if (script.parentNode) {
                      script.parentNode.insertBefore(newScript, script);
                      script.parentNode.removeChild(script);
                    }
                  } catch (err) {
                    // إذا فشلت الطريقة أعلاه، نتجاهل الخطأ ونستمر
                    console.warn('لم يتم تغيير سمة src لأسباب أمنية');
                  }
                }
              });
            }
          });
        });
        
        // بدء مراقبة التغييرات في الصفحة
        observer.observe(document, { childList: true, subtree: true });
      }
      
      // حماية كائن env.js
      if (window.ENV_CONFIG) {
        Object.defineProperty(window, 'ENV_CONFIG', {
          configurable: false,
          writable: false
        });
      }
    } catch (e) {
      // تجاهل الأخطاء
    }
  }

  // تفعيل المراقبة
  window.addEventListener('resize', checkDevToolsStatus);
  setInterval(checkDevToolsStatus, 1000);

  // إضافة حدث للكشف عن مفاتيح وحدة التحكم (F12)
  document.addEventListener('keydown', function(e) {
    // F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C
    if (
      e.keyCode === 123 || 
      (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67))
    ) {
      disableConsoleOutput();
      sanitizeSensitiveData();
      e.preventDefault();
    }
  });
  
  // التحقق من أن المتصفح مدعوم وآمن
  function checkBrowserSecurityStatus() {
    try {
      // التحقق من وضع التصفح الخاص
      const isPrivateBrowsing = !window.indexedDB;
      if (isPrivateBrowsing && isProduction) {
        // يمكن تسجيل هذا أو التعامل معه بطريقة خاصة
      }
      
      // التحقق من وجود إضافات متصفح قد تعترض البيانات
      if (isProduction) {
        setTimeout(() => {
          // اختبار اعتراض وحدة التحكم
          const randomValue = Math.random().toString(36);
          const testConsoleMsg = `security-test-${randomValue}`;
          
          // تعيين معالج لاعتراض رسائل الأخطاء
          const originalConsoleError = console.error;
          let interceptCounter = 0;
          console.error = function(msg) {
            if (msg === testConsoleMsg) {
              interceptCounter++;
            }
            return originalConsoleError.apply(console, arguments);
          };
          
          // اختبار الاعتراض
          console.error(testConsoleMsg);
          
          setTimeout(() => {
            // إذا كانت هناك أكثر من مكالمة، فقد تكون هناك إضافة تعترض
            if (interceptCounter > 1) {
              // وجود اعتراض محتمل - قم بتعزيز الأمان
              disableConsoleOutput();
              sanitizeSensitiveData();
            }
            
            // استعادة وظيفة console.error الأصلية
            console.error = originalConsoleError;
          }, 100);
        }, 1000);
      }
    } catch (e) {
      // تجاهل الأخطاء
    }
  }
  
  // تنفيذ فحص أمان المتصفح
  checkBrowserSecurityStatus();
}

// تنفيذ التدابير الأمنية
setupSecurityProtection();

// تثبيت مكون React الرئيسي مع مزود الأمان
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <SecurityProvider>
      <App />
    </SecurityProvider>
  </React.StrictMode>
);

// قياس الأداء
reportWebVitals();

// تسجيل service worker فقط في الإنتاج لتجنب مشاكل التطوير
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  navigator.serviceWorker.register('/firebase-messaging-sw.js')
    .then(function(registration) {
      console.log('Service Worker تم تسجيله بنجاح:', registration.scope);
    })
    .catch(function(error) {
      console.log('فشل تسجيل Service Worker:', error);
    });
}